import type {
    EventHandler,
    KeyboardEvent,
    KeyboardEventHandler,
    MouseEventHandler,
    SyntheticEvent,
} from 'react';

// https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key/Key_Values
export const ENTER_KEY = 'Enter';
export const ESCAPE_KEY = 'Esc';
export const ESCAPE_KEY_FULL = 'Escape';
export const SPACE_KEY = ' ';

export const TAB_KEY_CODE = 9;
export const ENTER_KEY_CODE = 13;
export const ESCAPE_KEY_CODE = 27;
export const SPACE_KEY_CODE = 32;
export const ZERO_KEY_CODE = 48;
export const NINE_KEY_CODE = 57;
export const A_KEY_CODE = 65;
export const Z_KEY_CODE = 90;

// https://www.w3.org/TR/wai-aria/#role_definitions
export const ROLE = {
    BUTTON: 'button',
    LINK: 'link',
    MENU: 'menu',
};

export interface A11yClickProps {
    onKeyUp: KeyboardEventHandler<HTMLElement>;
    onClick: MouseEventHandler<HTMLElement>;
    tabIndex: number;
    role: string;
}

function getKeyboardEvent(event: KeyboardEvent<HTMLElement>): string | number {
    const { keyCode, key } = event;
    return key || keyCode;
}

function keyboardOpenEventHandler(handler: KeyboardEventHandler) {
    return (event: KeyboardEvent<HTMLElement>) => {
        const eventKey = getKeyboardEvent(event);

        if ([ENTER_KEY_CODE, ENTER_KEY, SPACE_KEY_CODE, SPACE_KEY].includes(eventKey)) {
            handler(event);
        }
    };
}
/**
 * Returns an object containing props for A11y click handling.
 *
 * @param onClick - The event handler to be called on mouse click.
 * @param [role='button'] - The ARIA role of the element.
 * @param [tabIndex=0] - The tabindex attribute value.
 *
 * @example
 * const Button = () => {
 *   const handleClick = (event) => {
 *     logger.log('Button clicked!');
 *   };
 *   return (
 *     <button
 *       {...getA11yClickProps(handleClick, ROLE.BUTTON, 0)}
 *       aria-label="Click me!"
 *     >
 *       Click me!
 *     </button>
 *   );
 * };
 */
export function getA11yClickProps(
    onClick: MouseEventHandler,
    role = ROLE.BUTTON,
    tabIndex = 0,
): A11yClickProps {
    return {
        onKeyUp: keyboardOpenEventHandler(onClick as EventHandler<SyntheticEvent>),
        onClick,
        tabIndex,
        role,
    };
}
